import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { LEAGUES, resolveSupportedLeagueCode } from '../utils/leagues';
import { formatStandingRank } from '../utils/standingRankDisplay';
import './Auth.css';
import './Standings.css';
import './PrivateLeagues.css';
import { LeagueClashFixtures } from '../components/clash/LeagueClashFixtures';
import TierPyramidPopover from '../components/TierPyramidPopover';
import { useAuth } from '../context/AuthContext';
import { resolvePreferredLeagues, sortByLeaguePreference } from '../utils/leaguePreference';
import LoadingScreen from '../components/LoadingScreen';

const DAY_MS = 24 * 60 * 60 * 1000;
const ARCHIVE_VISIBLE_DAYS = 10;

function competitionForLeague(league) {
  const code =
    resolveSupportedLeagueCode(league.leagueCode) ||
    resolveSupportedLeagueCode(league.leagueType);
  if (!code) return { code: null, name: null };
  const def = LEAGUES.find((l) => l.code === code);
  return { code, name: def?.name || code };
}

function tierLabelFromCell(tierCell) {
  if (!tierCell || typeof tierCell !== 'string') return null;
  const [tier, division] = tierCell.split('-');
  if (!tier || !division) return null;
  return `Tier ${tier}, Division ${division}`;
}

function archiveDaysRemaining(finishedAt) {
  if (finishedAt == null || Number.isNaN(Number(finishedAt))) return null;
  const end = Number(finishedAt) + ARCHIVE_VISIBLE_DAYS * DAY_MS;
  return Math.max(0, Math.ceil((end - Date.now()) / DAY_MS));
}

function seasonSortKey(league) {
  const raw = String(league.seasonCode || league.leagueType || league.leagueCode || '');
  const year = raw.match(/(\d{4})/);
  return year ? Number(year[1]) : 0;
}

function splitSortKey(league) {
  if (league.tierSplitIndex != null && league.tierSplitIndex !== '') {
    const split = Number(league.tierSplitIndex);
    if (Number.isFinite(split)) return split;
  }
  const match = String(league.leagueId || '').match(/#(\d+)$/);
  return match ? Number(match[1]) : 0;
}

/** One entry per competition. Chain is oldest finished split → current league. */
function groupTieredLeagues(leagues) {
  const groups = [];
  const indexByCode = new Map();
  for (const league of leagues) {
    const code =
      resolveSupportedLeagueCode(league.leagueCode || league.leagueType) || league.leagueId;
    let group = indexByCode.get(code);
    if (!group) {
      group = { code, leagues: [] };
      indexByCode.set(code, group);
      groups.push(group);
    }
    group.leagues.push(league);
  }
  for (const group of groups) {
    group.leagues.sort((a, b) => {
      const yearDiff = seasonSortKey(a) - seasonSortKey(b);
      if (yearDiff !== 0) return yearDiff;
      const splitDiff = splitSortKey(a) - splitSortKey(b);
      if (splitDiff !== 0) return splitDiff;
      const aFinished = a.status === 'f' ? 0 : 1;
      const bFinished = b.status === 'f' ? 0 : 1;
      return aFinished - bFinished;
    });
  }
  return groups;
}

function defaultTierChainIndex(chain) {
  for (let i = chain.length - 1; i >= 0; i -= 1) {
    if (chain[i].status !== 'f') return i;
  }
  return Math.max(0, chain.length - 1);
}

function LeagueCard({
  league,
  user,
  invitingLeagueId,
  setInvitingLeagueId,
  inviteUsername,
  setInviteUsername,
  inviting,
  onInvite,
  onShowPrevious,
  onShowNext,
  nextTierLabel,
}) {
  const { code: compCode, name: compName } = competitionForLeague(league);
  const finished = league.status === 'f';
  const daysLeft = finished ? archiveDaysRemaining(league.finishedAt) : null;
  const tierLabel = tierLabelFromCell(league.tierCell);
  const showTierLabel = league.isTierPyramidLeague && tierLabel;
  const primaryLabel = showTierLabel ? tierLabel : league.name;
  const clashRounds = league.clashRounds;

  return (
    <div className="sc-card pl-league-card" data-testid={`league-card-${league.leagueId}`}>
      <div className="pl-league-head">
        <div className="pl-league-head-text">
          <div className="pl-league-title-row">
            {onShowPrevious ? (
              <button
                type="button"
                className="pl-tier-nav-btn"
                onClick={onShowPrevious}
                aria-label="Previous tiered league"
              >
                ←
              </button>
            ) : null}
            <h3 className="pl-league-title">{primaryLabel}</h3>
            {onShowNext ? (
              <button
                type="button"
                className="pl-tier-nav-btn"
                onClick={onShowNext}
                aria-label={nextTierLabel || 'Current tiered league'}
              >
                →
              </button>
            ) : null}
          </div>
          <p className="pl-league-meta">
            <span className="pl-competition-chip">
              {compCode ? (
                <span className="pl-competition-flag-wrap" aria-hidden="true">
                  <img
                    src={`/flags/${String(compCode).toLowerCase()}.svg`}
                    alt=""
                    className="pl-competition-flag"
                  />
                </span>
              ) : null}
              <span>{compName}</span>
            </span>
          </p>
        </div>
        <div className="pl-league-head-actions">
          {league.isOwner && !finished ? (
            <button
              type="button"
              className="sc-btn sc-btn-secondary pl-league-invite-btn"
              onClick={() =>
                setInvitingLeagueId(invitingLeagueId === league.leagueId ? null : league.leagueId)
              }
            >
              {invitingLeagueId === league.leagueId ? 'Cancel' : 'Invite'}
            </button>
          ) : null}
          {league.isTierPyramidLeague && league.tierCell ? (
            <TierPyramidPopover
              tierCell={league.tierCell}
              tierSplitIndex={league.tierSplitIndex}
              competitionName={compName}
              tierSeasonRounds={league.tierSeasonRounds}
            />
          ) : null}
        </div>
      </div>

      {invitingLeagueId === league.leagueId && league.isOwner && !finished ? (
        <div className="pl-invite-panel">
          <form onSubmit={(e) => onInvite(e, league)} className="auth-form" style={{ gap: '0.75rem' }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor={`invite-${league.leagueId}`}>Username</label>
              <input
                id={`invite-${league.leagueId}`}
                type="text"
                className="form-input"
                value={inviteUsername}
                onChange={(e) => setInviteUsername(e.target.value)}
                required
                placeholder="Their username"
                autoComplete="off"
              />
            </div>
            <button type="submit" className="sc-btn sc-btn-primary" disabled={inviting}>
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
          </form>
        </div>
      ) : null}

      <div className="pl-league-body">
        {league.isTierPyramidLeague ? (
          <p className="pl-tier-hint">
            This table tracks your division ladder group.
            {league.tierSeasonRounds ? (
              <>
                {' '}
                <span className="pl-tier-round-progress">
                  Round {league.tierSeasonRounds.current} of {league.tierSeasonRounds.total}.
                </span>
              </>
            ) : null}
          </p>
        ) : null}
        <LeagueClashFixtures
          clashRounds={clashRounds}
          currentUsername={user?.username}
        />
        <div className="pl-badge-row">
          {league.isTierPyramidLeague ? (
            <span
              className="pl-badge pl-badge--tier"
              title="Promotion-style divisions for this competition—climb or drop between splits; separate from the global season table."
            >
              Division ladder
            </span>
          ) : null}
          {league.isOwner ? <span className="pl-badge pl-badge--owner">Owner</span> : null}
          {!finished ? <span className="pl-badge pl-badge--live">Active</span> : null}
          {finished ? <span className="pl-badge pl-badge--ended">Finished</span> : null}
          {finished && daysLeft != null ? (
            <span className="pl-badge pl-badge--archive">{daysLeft}d left in list</span>
          ) : null}
        </div>
      </div>

      <div className="pl-standings-inner">
        {Array.isArray(league.standings) && league.standings.length > 0 ? (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Player</th>
                  <th style={{ textAlign: 'right' }}>Pts</th>
                </tr>
              </thead>
              <tbody>
                {league.standings.map((standing) => (
                  <tr key={standing.username || standing.userId}>
                    <td style={{ width: '2.5rem' }}>
                      {formatStandingRank(standing.rank, {
                        showMedals: finished,
                        numberedSuffix: '',
                      })}
                    </td>
                    <td>
                      <Link
                        to={`/profile/${encodeURIComponent(standing.username)}`}
                        className="standings-player-link"
                      >
                        {standing.username}
                      </Link>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <strong>{standing.points ?? standing.totalPoints}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="form-hint" style={{ marginTop: '0.65rem' }}>
              {league.memberCount ?? league.standings.length} member{(league.memberCount ?? league.standings.length) !== 1 ? 's' : ''}
            </p>
          </>
        ) : (
          <p className="standings-empty">No standings yet.</p>
        )}
      </div>
    </div>
  );
}

function TieredLeagueEntry({ leagues, ...cardProps }) {
  const chainKey = leagues.map((league) => league.leagueId).join('|');
  const defaultIndex = defaultTierChainIndex(leagues);
  const [index, setIndex] = useState(defaultIndex);
  const [seenKey, setSeenKey] = useState(chainKey);
  if (seenKey !== chainKey) {
    setSeenKey(chainKey);
    setIndex(defaultIndex);
  }

  const safeIndex = Math.min(Math.max(index, 0), Math.max(leagues.length - 1, 0));
  const league = leagues[safeIndex];
  if (!league) return null;
  const nextLeague = safeIndex < leagues.length - 1 ? leagues[safeIndex + 1] : null;

  return (
    <LeagueCard
      {...cardProps}
      league={league}
      onShowPrevious={safeIndex > 0 ? () => setIndex(safeIndex - 1) : null}
      onShowNext={nextLeague ? () => setIndex(safeIndex + 1) : null}
      nextTierLabel={nextLeague && nextLeague.status !== 'f' ? 'Current tiered league' : 'Next tiered league'}
    />
  );
}

export function LeaguesPageLoading() {
  return (
    <div className="betting-page pl-page">
      <header className="betting-header pl-header">
        <div className="pl-header__text">
          <h1 className="betting-title">Leagues</h1>
          <p className="betting-subtitle">
            Division ladder groups, private mini-leagues with friends, invitations, and standings.
          </p>
        </div>
        <button type="button" className="sc-btn sc-btn-secondary sc-btn-sm pl-new-league-btn" disabled>
          New league
        </button>
      </header>
      <LoadingScreen />
    </div>
  );
}

const PrivateLeagues = () => {
  const { user, refreshUser } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [leagueName, setLeagueName] = useState('');
  const preferredCompetitions = useMemo(
    () => resolvePreferredLeagues(user?.lp, user?.ld),
    [user?.lp, user?.ld]
  );
  const preferredCompetitionCodes = useMemo(
    () => new Set(preferredCompetitions.map((league) => league.code)),
    [preferredCompetitions]
  );
  const [selectedLeagueCode, setSelectedLeagueCode] = useState(
    () => preferredCompetitions[0]?.code || LEAGUES[0].code
  );
  const [creating, setCreating] = useState(false);

  const [invitingLeagueId, setInvitingLeagueId] = useState(null);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviting, setInviting] = useState(false);

  const leagueCodeValue = useCallback((league) => league.leagueCode || league.leagueType, []);

  const visibleLeagues = useMemo(
    () =>
      leagues.filter((league) => {
        const code = resolveSupportedLeagueCode(leagueCodeValue(league));
        return code && preferredCompetitionCodes.has(code);
      }),
    [leagues, leagueCodeValue, preferredCompetitionCodes]
  );

  const privateLeagues = useMemo(
    () => sortByLeaguePreference(
      visibleLeagues.filter((l) => !l.isTierPyramidLeague),
      leagueCodeValue,
      user?.lp,
      user?.ld
    ),
    [visibleLeagues, leagueCodeValue, user?.lp, user?.ld]
  );

  const tieredLeagues = useMemo(
    () => sortByLeaguePreference(
      visibleLeagues.filter((l) => l.isTierPyramidLeague),
      leagueCodeValue,
      user?.lp,
      user?.ld
    ),
    [visibleLeagues, leagueCodeValue, user?.lp, user?.ld]
  );

  const tieredLeagueGroups = useMemo(
    () => groupTieredLeagues(tieredLeagues),
    [tieredLeagues]
  );

  const fetchMyLeagues = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/leagues/my');
      setLeagues(response.data.leagues || []);
      setPendingInvitations(response.data.pendingInvitations || []);
    } catch (err) {
      if (err.response?.status === 401) return;
      setError('Failed to load leagues');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyLeagues();
  }, [fetchMyLeagues]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(''), 4500);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (preferredCompetitionCodes.has(selectedLeagueCode)) return;
    setSelectedLeagueCode(preferredCompetitions[0]?.code || LEAGUES[0].code);
  }, [preferredCompetitionCodes, preferredCompetitions, selectedLeagueCode]);

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    if (!leagueName.trim()) {
      setError('League name is required');
      return;
    }

    try {
      setCreating(true);
      setError('');
      await api.post('/leagues/private', {
        name: leagueName,
        leagueCode: selectedLeagueCode
      });
      setSuccess('League created successfully.');
      setShowCreateForm(false);
      setLeagueName('');
      fetchMyLeagues();
    } catch (err) {
      if (err.response?.status === 401) return;
      setError(err.response?.data?.error || 'Failed to create league');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (e, league) => {
    e.preventDefault();
    if (!inviteUsername.trim()) {
      setError('Username is required');
      return;
    }

    try {
      setInviting(true);
      setError('');
      const lid = encodeURIComponent(league.leagueId);
      await api.post(`/leagues/${lid}/invite`, {
        username: inviteUsername.trim(),
        leagueType: league.leagueType
      });
      setSuccess(`Invitation sent to ${inviteUsername.trim()}.`);
      setInvitingLeagueId(null);
      setInviteUsername('');
    } catch (err) {
      if (err.response?.status === 401) return;
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRespondToInvitation = async (inv, action) => {
    try {
      setError('');
      await api.post('/leagues/invitations/respond', {
        leagueType: inv.leagueType,
        leagueId: inv.leagueId,
        action
      });
      setSuccess(`Invitation ${action}ed.`);
      fetchMyLeagues();
      refreshUser();
    } catch (err) {
      if (err.response?.status === 401) return;
      setError(err.response?.data?.error || `Failed to ${action} invitation`);
    }
  };

  if (loading) {
    return <LeaguesPageLoading />;
  }

  return (
    <div className="betting-page pl-page">
      <header className="betting-header pl-header">
        <div className="pl-header__text">
          <h1 className="betting-title">Leagues</h1>
          <p className="betting-subtitle">
            Division ladder groups, private mini-leagues with friends, invitations, and standings.
          </p>
        </div>
        <button
          type="button"
          className="sc-btn sc-btn-secondary sc-btn-sm pl-new-league-btn"
          onClick={() => {
            setShowCreateForm((v) => !v);
            setError('');
          }}
        >
          {showCreateForm ? 'Close' : 'New league'}
        </button>
      </header>

      {error ? <div className="pl-flash pl-flash--error">{error}</div> : null}
      {success ? <div className="pl-flash pl-flash--success" role="status">{success}</div> : null}

      {showCreateForm ? (
        <div className="sc-card" style={{ marginBottom: '1.25rem' }}>
          <h2 className="standings-section-title" style={{ marginBottom: '0.75rem' }}>Create a league</h2>
          <p className="pl-league-meta" style={{ marginBottom: '0.5rem' }}>
            Uses the current season for the competition you pick. You become owner and can invite players.
          </p>
          <p className="pl-create-limit-hint">
            You can create up to three private leagues.
          </p>
          <form onSubmit={handleCreateLeague} className="auth-form">
            <div className="form-field">
              <label className="form-label" htmlFor="pl-name">League name</label>
              <input
                id="pl-name"
                type="text"
                className="form-input"
                value={leagueName}
                onChange={(e) => setLeagueName(e.target.value)}
                required
                placeholder="e.g. Office EPL"
              />
            </div>
            <div className="form-field">
              <label className="form-label" htmlFor="pl-comp">Competition</label>
              <select
                id="pl-comp"
                className="form-select"
                value={selectedLeagueCode}
                onChange={(e) => setSelectedLeagueCode(e.target.value)}
                required
              >
                {preferredCompetitions.map((league) => (
                  <option key={league.code} value={league.code}>
                    {league.name}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="sc-btn sc-btn-primary" disabled={creating}>
              {creating ? 'Creating…' : 'Create league'}
            </button>
          </form>
        </div>
      ) : null}

      {pendingInvitations.length > 0 ? (
        <section className="pl-section" data-testid="invitations-section">
          <h2 className="pl-section-title">Invitations ({pendingInvitations.length})</h2>
          <div className="pl-invite-list">
            {pendingInvitations.map((inv) => {
              const { name: competitionName } = competitionForLeague({
                leagueType: inv.seasonCode || inv.leagueType
              });
              return (
                <div
                  key={`${inv.leagueType}-${inv.leagueId}`}
                  className="sc-card pl-invite-card"
                  data-testid={`invite-card-${inv.leagueId}`}
                >
                  <div className="pl-invite-card-text">
                    <h3 className="pl-league-title">{inv.leagueName}</h3>
                    {competitionName ? (
                      <p className="pl-league-meta">{competitionName}</p>
                    ) : null}
                    <p className="pl-invite-from">From {inv.inviterUsername}</p>
                  </div>
                  <div className="pl-invite-actions">
                    <button
                      type="button"
                      className="sc-btn sc-btn-primary"
                      onClick={() => handleRespondToInvitation(inv, 'accept')}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      className="sc-btn sc-btn-secondary"
                      onClick={() => handleRespondToInvitation(inv, 'reject')}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      <div data-testid="leagues-list">
        {visibleLeagues.length === 0 ? (
          <section className="pl-section">
            <div className="sc-card pl-empty">
              <strong>No leagues yet</strong>
              Create one above, or accept an invitation when a friend adds you.
            </div>
          </section>
        ) : (
          <>
            {privateLeagues.length > 0 ? (
              <section className="pl-section" data-testid="private-leagues-section">
                <h2 className="pl-section-title">Private leagues ({privateLeagues.length})</h2>
                {privateLeagues.map((league) => (
                  <LeagueCard
                    key={league.leagueId}
                    league={league}
                    user={user}
                    invitingLeagueId={invitingLeagueId}
                    setInvitingLeagueId={setInvitingLeagueId}
                    inviteUsername={inviteUsername}
                    setInviteUsername={setInviteUsername}
                    inviting={inviting}
                    onInvite={handleInvite}
                  />
                ))}
              </section>
            ) : null}
            {tieredLeagueGroups.length > 0 ? (
              <section className="pl-section" data-testid="tiered-leagues-section">
                <h2 className="pl-section-title">Tiered leagues ({tieredLeagueGroups.length})</h2>
                {tieredLeagueGroups.map((group) => (
                  <TieredLeagueEntry
                    key={group.code}
                    leagues={group.leagues}
                    user={user}
                    invitingLeagueId={invitingLeagueId}
                    setInvitingLeagueId={setInvitingLeagueId}
                    inviteUsername={inviteUsername}
                    setInviteUsername={setInviteUsername}
                    inviting={inviting}
                    onInvite={handleInvite}
                  />
                ))}
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default PrivateLeagues;
